const OVERRIDE_STYLE_ID = "rio-assist-host-overrides";

let cleanupWidgetUiOverrides: (() => void) | null = null;

export const cleanupWidgetUiOverridesIfAny = () => {
  if (cleanupWidgetUiOverrides) {
    cleanupWidgetUiOverrides();
    cleanupWidgetUiOverrides = null;
  }
};

let brandTextsPatched = false;

const patchBrandTexts = () => {
  const widgetTitle = process.env.REACT_APP_RIO_ASSIST_TITLE;
  const prototype = window.customElements.get("rio-assist-widget")?.prototype;
  if (brandTextsPatched || !widgetTitle || !prototype) {
    return;
  }

  const rebrand = (value: unknown) =>
    typeof value === "string" ? value.replaceAll("UptAIme Assist", widgetTitle) : value;

  const loadingLabel = Object.getOwnPropertyDescriptor(prototype, "loadingLabel");
  if (loadingLabel?.get) {
    const originalGet = loadingLabel.get;
    Object.defineProperty(prototype, "loadingLabel", {
      ...loadingLabel,
      get(this: HTMLElement) {
        return rebrand(originalGet.call(this));
      },
    });
  }

  const errorMessage = Object.getOwnPropertyDescriptor(prototype, "errorMessage");
  if (errorMessage?.set) {
    const originalSet = errorMessage.set;
    Object.defineProperty(prototype, "errorMessage", {
      ...errorMessage,
      set(this: HTMLElement, value: unknown) {
        originalSet.call(this, rebrand(value));
      },
    });
  }

  brandTextsPatched = true;
};

export const applyWidgetUiOverrides = (host: HTMLElement) => {
  cleanupWidgetUiOverridesIfAny();
  patchBrandTexts();

  let observer: MutationObserver | null = null;

  const applyOverrides = () => {
    const shadowRoot = host.shadowRoot;
    if (!shadowRoot) {
      return false;
    }

    const floatingLabelImage = shadowRoot.querySelector(".floating-button__label-image");

    const widgetTitle = process.env.REACT_APP_RIO_ASSIST_TITLE;
    const fullscreenBrand = shadowRoot.querySelector(".fullscreen-header__brand");
    if (widgetTitle && fullscreenBrand && fullscreenBrand.textContent !== widgetTitle) {
      fullscreenBrand.textContent = widgetTitle;
    }

    shadowRoot.querySelectorAll('[aria-label*="UptAIme Assist"]').forEach((element) => {
      const label = element.getAttribute("aria-label") ?? "";
      if (widgetTitle) {
        element.setAttribute("aria-label", label.replace("UptAIme Assist", widgetTitle));
      }
    });

    const floatingButton = shadowRoot.querySelector<HTMLElement>(".floating-button");
    if (floatingButton && !floatingButton.dataset.dragGuard) {
      floatingButton.dataset.dragGuard = "true";
      floatingButton.addEventListener("dragstart", (event) => event.preventDefault());
    }
    floatingButton?.querySelectorAll("img").forEach((image) => {
      if (image.draggable) {
        image.draggable = false;
      }
    });

    let overrideStyle = shadowRoot.querySelector(`#${OVERRIDE_STYLE_ID}`);
    if (!overrideStyle) {
      overrideStyle = document.createElement("style");
      overrideStyle.id = OVERRIDE_STYLE_ID;
      overrideStyle.textContent = `
        .consultant-agent__button {
          display: none !important;
        }

        .floating-button__box-bg {
          display: none !important;
        }

        .floating-button {
          width: 64px !important;
          padding: 0 !important;
          justify-content: center !important;
        }

        .floating-button:hover {
          width: 330px !important;
          padding: 0 16px 0 8px !important;
          justify-content: flex-start !important;
          gap: 8px !important;
        }

        .floating-button .floating-button__label-image {
          max-width: 0 !important;
          height: 50px !important;
          opacity: 0 !important;
          transform: translateX(-5px) !important;
        }

        .floating-button:hover .floating-button__label-image {
          max-width: 250px !important;
          opacity: 1 !important;
          transform: translateX(-3px) !important;
        }
      `;
      shadowRoot.appendChild(overrideStyle);
    }

    return Boolean(floatingLabelImage);
  };

  const startObserver = () => {
    if (!host.shadowRoot || observer) {
      return;
    }

    observer = new MutationObserver(() => {
      applyOverrides();
    });
    observer.observe(host.shadowRoot, { childList: true, subtree: true, attributes: true });
  };

  const attached = applyOverrides();
  startObserver();

  if (!attached) {
    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;
      applyOverrides();
      startObserver();
      if (observer || attempts >= 20) {
        window.clearInterval(intervalId);
      }
    }, 150);

    cleanupWidgetUiOverrides = () => {
      window.clearInterval(intervalId);
      observer?.disconnect();
      cleanupWidgetUiOverrides = null;
    };
    return;
  }

  cleanupWidgetUiOverrides = () => {
    observer?.disconnect();
    cleanupWidgetUiOverrides = null;
  };
};
